import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import Enums from "@/enums";
import SectionEditModal from "@modals/SectionEditModal";
import ContentEditModal from "@modals/ContentEditModal";
import HelpEditModal from "@modals/HelpEditModal";
import ReportDefinitionModal from "@modals/ReportDefinitionModal";
import LanguageValuesEditModal, {
  type LanguageValuesEditMode,
} from "@modals/LanguageValuesEditModal";
import { decodeHelpText } from "@/utils/helpContentCodec";
import { readPermIdsFromSession } from "@/i18n/sessionUser";
import { opensLanguageValuesModal } from "@/utils/languageValuesEdit";

function readAssignedLanguageIdsFromSession(): number[] {
  try {
    const userData = window.sessionStorage.getItem("User");
    if (!userData) return [];

    const decodedUser = JSON.parse(atob(userData));
    const raw = decodedUser?.assignedLanguageIds ?? decodedUser?.AssignedLanguageIds;
    if (!Array.isArray(raw)) return [];

    return raw
      .map((id: unknown) => Number(id))
      .filter((id: number) => Number.isFinite(id) && id > 1);
  } catch {
    return [];
  }
}

interface AdminTranslationsTabProps {
  tabData?: {
    activeTab?: string;
    updateSpinner?: (show: boolean) => void;
    adminPerms?: number[];
  };
}

type TranslatorContentLanguage = {
  companyContentLanguageId?: number | null;
  helpContentLanguageId?: number | null;
  fieldTranslationId?: number | null;
  sectionContentLanguageId?: number | null;
  sectionControlLanguageId?: number | null;
  reportLanguageId?: number | null;
  content: string;
  title?: string | null;
  languageId: number;
  languageName: string;
  humanEdited: boolean;
};

type TranslatorRow = {
  contentType: string;
  context: string;
  fieldTranslationKey?: string | null;
  companyId?: number | null;
  companyContentId?: number | null;
  helpContentId?: number | null;
  sectionContentId?: number | null;
  sectionControlId?: number | null;
  reportDefinitionId?: number | null;
  supportsInlineEdit?: boolean;
  contentFormat?: number | null;
  contentFormatName?: string | null;
  languages: TranslatorContentLanguage[];
};

function looksLikeJsonContent(value?: string | null): boolean {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return false;
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

function normalizeContentFormat(raw: unknown): number {
  if (raw === Enums.CONTENT_FORMAT.Text || raw === 0 || raw === "0" || raw === "Text") {
    return Enums.CONTENT_FORMAT.Text;
  }
  if (raw === Enums.CONTENT_FORMAT.JSON || raw === 2 || raw === "2" || raw === "JSON") {
    return Enums.CONTENT_FORMAT.JSON;
  }
  if (raw === Enums.CONTENT_FORMAT.HTML || raw === 1 || raw === "1" || raw === "HTML") {
    return Enums.CONTENT_FORMAT.HTML;
  }
  return Enums.CONTENT_FORMAT.HTML;
}

function normalizeRowLanguages(raw: unknown): TranslatorContentLanguage[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const languageId = Number(record.languageId ?? record.LanguageId ?? 0);
      if (!Number.isFinite(languageId) || languageId <= 0) return null;

      return {
        companyContentLanguageId: (record.companyContentLanguageId ?? record.CompanyContentLanguageId ?? null) as number | null | undefined,
        helpContentLanguageId: (record.helpContentLanguageId ?? record.HelpContentLanguageId ?? null) as number | null | undefined,
        fieldTranslationId: (record.fieldTranslationId ?? record.FieldTranslationId ?? null) as number | null | undefined,
        sectionContentLanguageId: (record.sectionContentLanguageId ?? record.SectionContentLanguageId ?? null) as number | null | undefined,
        sectionControlLanguageId: (record.sectionControlLanguageId ?? record.SectionControlLanguageId ?? null) as number | null | undefined,
        reportLanguageId: (record.reportLanguageId ?? record.ReportLanguageId ?? null) as number | null | undefined,
        content: String(record.content ?? record.Content ?? ""),
        title: (record.title ?? record.Title ?? null) as string | null | undefined,
        languageId,
        languageName: String(record.languageName ?? record.LanguageName ?? ""),
        humanEdited: Boolean(record.humanEdited ?? record.HumanEdited ?? false),
      } satisfies TranslatorContentLanguage;
    })
    .filter(Boolean) as TranslatorContentLanguage[];
}

function getLanguageEntry(row: TranslatorRow, languageId: number): TranslatorContentLanguage | undefined {
  return row.languages.find((lang) => lang.languageId === languageId);
}

function getEnglishContent(row: TranslatorRow): string {
  return getLanguageEntry(row, 1)?.content ?? "";
}

function getEnglishTitle(row: TranslatorRow): string {
  return getLanguageEntry(row, 1)?.title ?? "";
}

function getRowContentFormat(row: TranslatorRow): number {
  const raw = row.contentFormat;
  if (raw !== null && raw !== undefined) {
    return normalizeContentFormat(raw);
  }

  if (row.contentType === "Help") {
    return Enums.CONTENT_FORMAT.Text;
  }

  const englishContent = getEnglishContent(row);
  if (looksLikeJsonContent(englishContent)) {
    return Enums.CONTENT_FORMAT.JSON;
  }

  if (row.supportsInlineEdit) {
    return Enums.CONTENT_FORMAT.Text;
  }

  return Enums.CONTENT_FORMAT.HTML;
}

function normalizeTranslatorRow(raw: Record<string, unknown>): TranslatorRow {
  return {
    contentType: String(raw.contentType ?? raw.ContentType ?? ""),
    context: String(raw.context ?? raw.Context ?? ""),
    fieldTranslationKey: (raw.fieldTranslationKey ?? raw.FieldTranslationKey ?? null) as string | null | undefined,
    companyId: (raw.companyId ?? raw.CompanyId ?? null) as number | null | undefined,
    companyContentId: (raw.companyContentId ?? raw.CompanyContentId ?? null) as number | null | undefined,
    helpContentId: (raw.helpContentId ?? raw.HelpContentId ?? null) as number | null | undefined,
    sectionContentId: (raw.sectionContentId ?? raw.SectionContentId ?? null) as number | null | undefined,
    sectionControlId: (raw.sectionControlId ?? raw.SectionControlId ?? null) as number | null | undefined,
    reportDefinitionId: (raw.reportDefinitionId ?? raw.ReportDefinitionId ?? null) as number | null | undefined,
    supportsInlineEdit: Boolean(raw.supportsInlineEdit ?? raw.SupportsInlineEdit),
    contentFormat: (() => {
      const rawFormat = raw.contentFormat ?? raw.ContentFormat;
      if (rawFormat === null || rawFormat === undefined || rawFormat === "") return null;
      return normalizeContentFormat(rawFormat);
    })(),
    contentFormatName: (raw.contentFormatName ?? raw.ContentFormatName ?? null) as string | null | undefined,
    languages: normalizeRowLanguages(raw.languages ?? raw.Languages),
  };
}

function formatHelpEnglishCellDisplay(title?: string | null, text?: string | null) {
  const decodedTitle = decodeHelpText(title ?? "").trim();
  const decodedContent = decodeHelpText(text ?? "").trim();
  const parts: string[] = [];
  if (decodedTitle) parts.push(decodedTitle);
  if (decodedContent) parts.push("HTML");
  return parts.join("\n");
}

function formatEnglishCellDisplay(row: TranslatorRow) {
  if (getRowContentFormat(row) === Enums.CONTENT_FORMAT.JSON) return "JSON";
  if (row.contentType === "Company" && getRowContentFormat(row) === Enums.CONTENT_FORMAT.HTML) {
    return "HTML";
  }
  if (row.contentType === "Help") {
    return formatHelpEnglishCellDisplay(getEnglishTitle(row), getEnglishContent(row));
  }
  return getEnglishContent(row);
}

function formatRowId(row: TranslatorRow): string | number {
  if (row.contentType === "Company" && row.companyContentId) return row.companyContentId;
  if (row.contentType === "Section" && row.sectionContentId) return row.sectionContentId;
  if (row.contentType === "SectionControl" && row.sectionControlId) return row.sectionControlId;
  if (row.contentType === "Help" && row.helpContentId) return row.helpContentId;
  if (row.contentType === "Report" && row.reportDefinitionId) return row.reportDefinitionId;
  return "—";
}

export function translationRowMatchesSearch(row: TranslatorRow, searchText: string): boolean {
  const needle = searchText.trim().toLowerCase();
  if (!needle) return true;

  const haystack = [
    String(formatRowId(row)),
    row.contentType,
    row.context,
    row.fieldTranslationKey ?? "",
    formatEnglishCellDisplay(row),
    getEnglishContent(row),
  ]
    .join("\n")
    .toLowerCase();

  return haystack.includes(needle);
}

function normalizePerm(perm: number | { value?: number; permId?: number }) {
  return Number(typeof perm === "object" ? (perm?.value ?? perm?.permId ?? 0) : perm);
}

function rowKey(row: TranslatorRow, index: number) {
  return [
    row.contentType,
    row.context,
    row.fieldTranslationKey ?? "",
    row.companyContentId ?? 0,
    row.helpContentId ?? 0,
    row.sectionContentId ?? 0,
    row.sectionControlId ?? 0,
    row.reportDefinitionId ?? 0,
    index,
  ].join(":");
}

export default function AdminTranslationsTab(props: AdminTranslationsTabProps) {
  const tabData = props?.tabData;
  const updateSpinnerRef = useRef(tabData?.updateSpinner ?? (() => {}));
  updateSpinnerRef.current = tabData?.updateSpinner ?? (() => {});
  const activeTab = tabData?.activeTab;
  const adminPerms = tabData?.adminPerms ?? [];

  const normalizedPerms = Array.isArray(adminPerms) ? adminPerms.map(normalizePerm) : [];
  const canTranslate = normalizedPerms.includes(Enums.ROLE_PERMS.SystemAdmin)
    || normalizedPerms.includes(Enums.ROLE_PERMS.Translator);
  const isSystemAdmin = normalizedPerms.includes(Enums.ROLE_PERMS.SystemAdmin)
    || readPermIdsFromSession().includes(Enums.ROLE_PERMS.SystemAdmin);

  const [rows, setRows] = useState<TranslatorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [languageValuesOpen, setLanguageValuesOpen] = useState(false);
  const [languageValuesMode, setLanguageValuesMode] = useState<LanguageValuesEditMode>("Field");
  const [languageValuesRow, setLanguageValuesRow] = useState<TranslatorRow | null>(null);

  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [sectionContentId, setSectionContentId] = useState<number | null>(null);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [companyContentId, setCompanyContentId] = useState<number | null>(null);
  const [companyContentCompanyId, setCompanyContentCompanyId] = useState<number>(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpEntry, setHelpEntry] = useState<Record<string, unknown> | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportDefinitionId, setReportDefinitionId] = useState<number | null>(null);

  const isOnThisTab = activeTab === "translations";
  const hasAssignedLanguages = isSystemAdmin || readAssignedLanguageIdsFromSession().length > 0;

  const loadGenerationRef = useRef(0);

  const loadTabData = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    updateSpinnerRef.current(true);
    try {
      const contentResp = await RightTimeApi.getAsync("/Translator/Content");
      if (generation !== loadGenerationRef.current) return;

      if (contentResp?.ok && contentResp.jsonData) {
        const loadedRows = Array.isArray(contentResp.jsonData.rows)
          ? contentResp.jsonData.rows
          : contentResp.jsonData.Rows ?? [];
        setRows(loadedRows.map((row: Record<string, unknown>) => normalizeTranslatorRow(row)));
      } else {
        toast.error("Failed to load translations.");
        setRows([]);
      }
    } catch (error) {
      if (generation !== loadGenerationRef.current) return;
      console.error("Error loading translations tab:", error);
      toast.error("Error loading translations.");
      setRows([]);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
        updateSpinnerRef.current(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isOnThisTab || !canTranslate) return;
    void loadTabData();
    return () => {
      loadGenerationRef.current += 1;
    };
  }, [isOnThisTab, canTranslate, loadTabData]);

  const filteredRows = useMemo(
    () => rows.filter((row) => translationRowMatchesSearch(row, searchText)),
    [rows, searchText]
  );

  if (!isOnThisTab || !canTranslate) return null;

  async function handleModalSaved() {
    await loadTabData();
  }

  async function openRowEditor(row: TranslatorRow) {
    if (opensLanguageValuesModal(row)) {
      const mode: LanguageValuesEditMode =
        row.contentType === "Company"
          ? "Company"
          : row.contentType === "SectionControl"
            ? "SectionControl"
            : "Field";
      setLanguageValuesMode(mode);
      setLanguageValuesRow(row);
      setLanguageValuesOpen(true);
      return;
    }
    if (row.contentType === "Section" && row.sectionContentId) {
      setSectionContentId(row.sectionContentId);
      setSectionModalOpen(true);
      return;
    }
    if (row.contentType === "Company" && row.companyContentId) {
      setCompanyContentId(row.companyContentId);
      setCompanyContentCompanyId(Number(row.companyId ?? 0));
      setContentModalOpen(true);
      return;
    }
    if (row.contentType === "Help" && row.helpContentId) {
      const allResp = await RightTimeApi.getAsync("/Help/All");
      const helpItems = Array.isArray(allResp?.jsonData) ? allResp.jsonData : [];
      const entry = helpItems.find((item: { id?: number }) => Number(item?.id) === Number(row.helpContentId)) ?? null;
      setHelpEntry(entry);
      setHelpModalOpen(true);
      return;
    }
    if (row.contentType === "Report" && row.reportDefinitionId) {
      setReportDefinitionId(row.reportDefinitionId);
      setReportModalOpen(true);
    }
  }

  return (
    <>
      {(!hasAssignedLanguages) && !loading && (
        <div className="emptyStateCell paddedCell translationsEmptyLanguages">
          No translation languages are assigned to your account. Contact an administrator to assign languages in User Edit.
        </div>
      )}

      <div className="tableWrapper dimensionTable fieldTranslationTable scrollable">
        <table>
          <caption data-help-id="Translations">
            <span className="title">Translations</span>
            <span className="captionRight">
              <label className="translationCatalogSearch" htmlFor="translationCatalogSearch">
                <span className="translationCatalogSearchLabel">Filter</span>
                <input
                  id="translationCatalogSearch"
                  type="search"
                  value={searchText}
                  size={10}
                  onChange={(e) => setSearchText(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            </span>
          </caption>
          <thead>
            <tr>
              <th className="centerAlignedCell">ID</th>
              <th>Type</th>
              <th>Context</th>
              <th>English</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="centerAlignedCell paddedCell">
                  Loading translations...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="emptyStateCell paddedCell">
                  {rows.length === 0
                    ? "No translation rows found."
                    : "No translations match your search."}
                </td>
              </tr>
            ) : (
              filteredRows.map((row, index) => {
                const key = rowKey(row, index);
                const englishDisplay = formatEnglishCellDisplay(row);
                return (
                  <tr
                    key={key}
                    className="translationCatalogRow"
                    onClick={() => void openRowEditor(row)}
                  >
                    <td className="centerAlignedCell">{formatRowId(row)}</td>
                    <td>{row.contentType}</td>
                    <td className="wrappedTextCell">{row.context}</td>
                    <td className="wrappedTextCell">
                      <pre className="translationPreview">{englishDisplay}</pre>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <LanguageValuesEditModal
        isOpen={languageValuesOpen}
        mode={languageValuesMode}
        contextLabel={languageValuesRow?.context ?? ""}
        fieldTranslationKey={languageValuesRow?.fieldTranslationKey}
        englishText={languageValuesRow ? getEnglishContent(languageValuesRow) : ""}
        sectionControlId={languageValuesRow?.sectionControlId}
        companyId={languageValuesRow?.companyId}
        companyContentId={languageValuesRow?.companyContentId}
        initialLanguages={languageValuesRow?.languages}
        canEditEnglish={languageValuesMode === "SectionControl" ? isSystemAdmin : true}
        onClose={(saved) => {
          setLanguageValuesOpen(false);
          setLanguageValuesRow(null);
          if (saved) void handleModalSaved();
        }}
      />

      <SectionEditModal
        isOpen={sectionModalOpen}
        onClose={() => {
          setSectionModalOpen(false);
          setSectionContentId(null);
          void handleModalSaved();
        }}
        sectionContentId={sectionContentId}
        updateSpinner={updateSpinnerRef.current}
      />

      <ContentEditModal
        isOpen={contentModalOpen}
        onClose={() => {
          setContentModalOpen(false);
          setCompanyContentId(null);
          void handleModalSaved();
        }}
        companyId={companyContentCompanyId}
        companyContentId={companyContentId ?? 0}
      />

      <HelpEditModal
        isOpen={helpModalOpen}
        onClose={() => {
          setHelpModalOpen(false);
          setHelpEntry(null);
          void handleModalSaved();
        }}
        helpEntry={helpEntry}
        onSaved={handleModalSaved}
      />

      <ReportDefinitionModal
        reportDefinitionId={reportModalOpen ? reportDefinitionId : null}
        onClose={() => {
          setReportModalOpen(false);
          setReportDefinitionId(null);
          void handleModalSaved();
        }}
        updateSpinner={updateSpinnerRef.current}
      />
    </>
  );
}
